/// <amd-module name="WidgetCommon/ModernWidgetTypes/ActionCreatorBase" />
import { DemandType } from "Reporting/Visuals/LayoutComponents/Widgets/Demands/DemandType";
import { IDemandHandler } from "Reporting/Visuals/LayoutComponents/Widgets/Demands/IDemandHandler";
import { IDataManager } from "Reporting/Visuals/LayoutComponents/Widgets/IDataManager";
import { LayoutActions } from "Reporting/Visuals/LayoutComponents/Widgets/LayoutActions";
import { LayoutState, MessageType, packMessageAsState } from "Reporting/Visuals/LayoutComponents/Widgets/LayoutState";
import { LayoutStore } from "Reporting/Visuals/LayoutComponents/Widgets/LayoutStore";
import { IVssPageContext } from "VSS/Platform/Context";

/**
 * Provides public API's for driving action behavior, which leads to transformations of state.
 *
 * Notably: Declaring data dependencies from consumers, initializing data state and handling user driven events.
 */
export abstract class ActionCreatorBase implements IDemandHandler {
    protected _context: IVssPageContext;
    protected _state: LayoutStore;
    protected _dataManager: IDataManager;
    protected _actions: LayoutActions;
    protected stringifyError: (e: any) => string;

    constructor(
        context: IVssPageContext,
        dataManager: IDataManager,
        data: LayoutStore,
        actions: LayoutActions,
        widgetTypeName: string,
        errorStringifier: (e: any) => string
    ) {
        this._context = context;
        this._state = data;
        this._dataManager = dataManager;
        this._actions = actions;
        this.stringifyError = errorStringifier;
    }

    /**
     *  Responsible for performs flux data-bootstrapping process.
     *  In particular, we get minimal format of data associated with current demands from data manager, and put out a ProvideData
     */
    public initialize(): void {
        this._actions.ProvideData.invoke(this._dataManager.getInitialState());
    }

    /**
     * Responsible for starting async load of demanded data.
     */
    public async requestData(): Promise<any> {
        await this._dataManager.getData().then(
            state => {
                this._actions.ProvideData.invoke(state);
                return Promise.resolve(null);
            },
            state => {
                this._actions.ProvideData.invoke(state);
                if (state.messageType === MessageType.WidgetError) {
                    return Promise.reject(state.message);
                } else {
                    const errorMessage = this.stringifyError(state);
                    return Promise.reject(errorMessage);
                }
            }
        );
    }

    public handleMessageState(messageType: MessageType, message?: string): void {
        const state: LayoutState = packMessageAsState(messageType, message);
        this._actions.ProvideData.invoke(state);
    }

    public registerDemand(demandType: DemandType): void {
        this._dataManager.registerDemand(demandType);
    }
}
